import { Activity, ChevronDown, ChevronRight, Clock, PenLine, Table2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { TimeReport, TimeReportGroup, TimeReportRow } from "@/api/queries";
import { ActivityBar } from "@/components/common/ActivityBar";
import { EmptyState } from "@/components/common/EmptyState";
import { StatCard } from "@/components/common/StatCard";
import { UserAvatar } from "@/components/common/UserAvatar";
import { WeekChart } from "@/components/common/WeekChart";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/input";
import { formatDuration, formatPeriod, formatShortDate, percentOf } from "@/lib/format";
import { cn } from "@/lib/utils";

// eslint-disable-next-line react-refresh/only-export-components
export const timeGroups: TimeReportGroup[] = ["user", "project", "task", "date"];

type Totals = { tracked: number; manual: number; active: number };

/** A row of the report tree: one group value with its subtotals. */
type Node = {
  key: string;
  level: number;
  row: TimeReportRow; // the first leaf row: carries the group values
  totals: Totals;
  children: Node[];
};

function keyOf(row: TimeReportRow, group: TimeReportGroup): string {
  switch (group) {
    case "user":
      return row.user?.id ?? "";
    case "project":
      return row.project?.id ?? "";
    case "task":
      return `${row.project?.id ?? ""}:${row.task?.id ?? ""}`;
    case "date":
      return row.date ?? "";
  }
}

/** Builds the tree; the server has already ordered the rows level by level. */
// eslint-disable-next-line react-refresh/only-export-components
export function buildTree(rows: TimeReportRow[], groups: TimeReportGroup[], level = 0, prefix = ""): Node[] {
  const group = groups[level];
  if (!group) return [];
  const nodes: Node[] = [];
  const byKey = new Map<string, { node: Node; rows: TimeReportRow[] }>();
  for (const row of rows) {
    const key = `${prefix}/${keyOf(row, group)}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = {
        node: { key, level, row, totals: { tracked: 0, manual: 0, active: 0 }, children: [] },
        rows: [],
      };
      byKey.set(key, entry);
      nodes.push(entry.node);
    }
    entry.rows.push(row);
    entry.node.totals.tracked += row.tracked_seconds;
    entry.node.totals.manual += row.manual_seconds;
    entry.node.totals.active += row.active_seconds;
  }
  for (const { node, rows: sub } of byKey.values()) {
    node.children = buildTree(sub, groups, level + 1, node.key);
  }
  return nodes;
}

function countNodes(nodes: Node[]): number {
  return nodes.reduce((n, node) => n + 1 + countNodes(node.children), 0);
}

type GroupPickerProps = {
  value: TimeReportGroup[];
  onChange: (groups: TimeReportGroup[]) => void;
};

/** Up to three grouping levels. */
export function GroupPicker({ value, onChange }: GroupPickerProps) {
  const { t } = useTranslation();
  const levels = [0, 1, 2];
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-muted">{t("reports.groupBy")}</span>
      {levels.map((i) => {
        if (i > value.length) return null;
        const used = value.filter((_, j) => j < i);
        const options = timeGroups.filter((g) => !used.includes(g));
        return (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="size-3.5 text-muted" aria-hidden="true" />}
            <NativeSelect
              aria-label={t("reports.groupLevel", { level: i + 1 })}
              value={value[i] ?? ""}
              onChange={(e) => {
                const next = value.slice(0, i);
                if (e.target.value) next.push(e.target.value as TimeReportGroup);
                // Keep the deeper levels that are still distinct.
                for (const g of value.slice(i + 1)) if (!next.includes(g)) next.push(g);
                onChange(next.slice(0, 3));
              }}
              className="h-8"
            >
              {i > 0 && <option value="">{t("reports.groups.none")}</option>}
              {options.map((g) => (
                <option key={g} value={g}>
                  {t(`reports.groups.${g}`)}
                </option>
              ))}
            </NativeSelect>
          </span>
        );
      })}
    </div>
  );
}

type TimeReportViewProps = {
  data: TimeReport;
  lang: string;
  today: string;
  toolbar: ReactNode;
  showPeople: boolean;
};

export function TimeReportView({ data, lang, today, toolbar, showPeople }: TimeReportViewProps) {
  const { t } = useTranslation();
  const total = data.totals.tracked_seconds + data.totals.manual_seconds;
  const tree = buildTree(data.rows, data.group_by);
  const workdays = data.days.filter((d) => d.tracked_seconds + d.manual_seconds > 0).length;
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState(countNodes(tree) <= 60);
  const isOpen = (key: string) => (expandAll ? !collapsed.has(key) : collapsed.has(key));
  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          label={t("reports.time.total")}
          icon={Clock}
          value={formatDuration(total, lang)}
          hint={
            showPeople
              ? t("reports.time.people", { count: data.totals.users })
              : t("reports.time.workdays", { count: workdays })
          }
        />
        <StatCard
          label={t("reports.time.manual")}
          icon={PenLine}
          value={formatDuration(data.totals.manual_seconds, lang)}
          hint={t("reports.time.manualShare", { percent: percentOf(data.totals.manual_seconds, total) })}
        />
        <StatCard
          label={t("reports.time.activity")}
          icon={Activity}
          value={`${percentOf(data.totals.active_seconds, data.totals.tracked_seconds)}%`}
          hint={t("reports.time.activityHint")}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>{t("reports.time.chartTitle")}</CardTitle>
          <CardDescription>
            {t("reports.time.chartHint", { period: formatPeriod(data.from, data.to, lang) })}
          </CardDescription>
        </CardHeader>
        <div className="px-5 pb-5">
          <WeekChart days={data.days} projects={data.projects} today={today} lang={lang} />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 pb-4">
          {toolbar}
          {tree.length > 0 && data.group_by.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setExpandAll(!expandAll);
                setCollapsed(new Set());
              }}
            >
              {expandAll ? t("reports.collapseAll") : t("reports.expandAll")}
            </Button>
          )}
        </CardHeader>
        {data.truncated && (
          <Alert variant="warning" className="mx-5 mb-4">
            {t("reports.truncated")}
          </Alert>
        )}
        {tree.length === 0 ? (
          <EmptyState icon={Table2} title={t("reports.empty")} description={t("reports.emptyHint")} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead className="bg-surface-muted text-xs text-muted">
                <tr>
                  <th className="px-5 py-2.5 font-medium">
                    {data.group_by.map((g) => t(`reports.groups.${g}`)).join(" / ")}
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">{t("reports.columns.hours")}</th>
                  <th className="px-3 py-2.5 text-right font-medium">{t("reports.columns.manual")}</th>
                  <th className="w-40 px-3 py-2.5 font-medium">{t("reports.columns.activity")}</th>
                  <th className="px-5 py-2.5 text-right font-medium">{t("reports.columns.share")}</th>
                </tr>
              </thead>
              <tbody>
                {tree.map((node) => (
                  <TreeRows
                    key={node.key}
                    node={node}
                    groups={data.group_by}
                    total={total}
                    lang={lang}
                    isOpen={isOpen}
                    toggle={toggle}
                  />
                ))}
              </tbody>
              <tfoot className="border-t bg-surface-muted/60 font-semibold">
                <tr>
                  <td className="px-5 py-2.5">
                    {showPeople
                      ? t("reports.totalPeople", { count: data.totals.users })
                      : t("reports.columns.total")}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{formatDuration(total, lang)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {data.totals.manual_seconds > 0 ? formatDuration(data.totals.manual_seconds, lang) : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <ActivityBar
                      percent={percentOf(data.totals.active_seconds, data.totals.tracked_seconds)}
                    />
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

type TreeRowsProps = {
  node: Node;
  groups: TimeReportGroup[];
  total: number;
  lang: string;
  isOpen: (key: string) => boolean;
  toggle: (key: string) => void;
};

function TreeRows({ node, groups, total, lang, isOpen, toggle }: TreeRowsProps) {
  const { t } = useTranslation();
  const open = node.children.length > 0 && isOpen(node.key);
  const worked = node.totals.tracked + node.totals.manual;
  const group = groups[node.level] ?? "user";
  const top = node.level === 0;
  return (
    <>
      <tr className={cn("border-t", !top && "text-[13px]")}>
        <td className="py-2 pr-3" style={{ paddingLeft: `${20 + node.level * 24}px` }}>
          <div className="flex min-w-0 items-center gap-1.5">
            {node.children.length > 0 ? (
              <Button
                variant="ghost"
                size="icon"
                className="size-6 shrink-0"
                aria-expanded={open}
                aria-label={open ? t("apps.hideDetails") : t("apps.showDetails")}
                onClick={() => toggle(node.key)}
              >
                {open ? <ChevronDown /> : <ChevronRight />}
              </Button>
            ) : (
              <span className="size-6 shrink-0" />
            )}
            <GroupLabel
              row={node.row}
              group={group}
              lang={lang}
              strong={top}
              underProject={groups.slice(0, node.level).includes("project")}
            />
          </div>
        </td>
        <td className={cn("px-3 py-2 text-right whitespace-nowrap tabular-nums", top && "font-semibold")}>
          {formatDuration(worked, lang)}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap text-muted tabular-nums">
          {node.totals.manual > 0 ? formatDuration(node.totals.manual, lang) : "—"}
        </td>
        <td className="px-3 py-2">
          {node.totals.tracked > 0 ? (
            <ActivityBar percent={percentOf(node.totals.active, node.totals.tracked)} />
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="px-5 py-2 text-right text-muted tabular-nums">{shareOf(worked, total, lang)}</td>
      </tr>
      {open &&
        node.children.map((child) => (
          <TreeRows
            key={child.key}
            node={child}
            groups={groups}
            total={total}
            lang={lang}
            isOpen={isOpen}
            toggle={toggle}
          />
        ))}
    </>
  );
}

function shareOf(part: number, total: number, lang: string): string {
  if (total <= 0) return "—";
  return new Intl.NumberFormat(lang, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 })
    .format(part / total)
    .replace(/\s%/, "%");
}

function GroupLabel({
  row,
  group,
  lang,
  strong,
  underProject,
}: {
  row: TimeReportRow;
  group: TimeReportGroup;
  lang: string;
  strong: boolean;
  underProject: boolean;
}) {
  const { t } = useTranslation();
  const weight = strong ? "font-semibold" : "font-medium";
  switch (group) {
    case "user":
      return row.user ? (
        <span className="flex min-w-0 items-center gap-2">
          <UserAvatar name={row.user.full_name} className="size-6 text-[10px]" />
          <span className={cn("truncate", weight)}>{row.user.full_name}</span>
        </span>
      ) : null;
    case "project":
      return (
        <span className="flex min-w-0 items-center gap-2">
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: row.project?.color }}
            aria-hidden="true"
          />
          <span className={cn("truncate", weight)}>{row.project?.name}</span>
        </span>
      );
    case "task":
      return (
        <span className="flex min-w-0 flex-col">
          <span className={cn("truncate", weight, !row.task && "text-muted")}>
            {row.task ? row.task.title || "—" : t("manual.noTask")}
          </span>
          {!underProject && (
            <span className="flex items-center gap-1.5 text-xs text-muted">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: row.project?.color }}
                aria-hidden="true"
              />
              {row.project?.name}
            </span>
          )}
        </span>
      );
    case "date":
      return (
        <span className={cn("first-letter:uppercase", weight)}>
          {row.date && formatShortDate(row.date, lang)}
        </span>
      );
  }
}
